import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import MDTypography from "components/MDTypography";
import usePortalTheme from "./usePortalTheme";

export function PortalCard({ title, subtitle, children, action, accentTop, sx }) {
  const p = usePortalTheme();
  return (
    <Box
      sx={{
        bgcolor: p.surface,
        border: `1px solid ${p.lineStrong}`,
        borderRadius: `${p.radius}px`,
        borderTop: accentTop ? `3px solid ${p.accent}` : undefined,
        boxShadow: p.shadow,
        p: { xs: 2, md: 2.5 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            mb: subtitle || children ? 1.25 : 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <MDTypography variant="h6" sx={{ color: p.ink, fontWeight: 800, lineHeight: 1.2 }}>
                {title}
              </MDTypography>
            ) : null}
            {subtitle ? (
              <MDTypography variant="body2" sx={{ color: p.muted, mt: 0.5, lineHeight: 1.55 }}>
                {subtitle}
              </MDTypography>
            ) : null}
          </Box>
          {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

PortalCard.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  action: PropTypes.node,
  accentTop: PropTypes.bool,
  sx: PropTypes.object,
};

PortalCard.defaultProps = {
  title: null,
  subtitle: null,
  children: null,
  action: null,
  accentTop: true,
  sx: {},
};

export function PortalMetric({ label, value, hint, icon }) {
  const p = usePortalTheme();
  return (
    <Box
      sx={{
        bgcolor: p.surface,
        border: `1px solid ${p.lineStrong}`,
        borderRadius: `${p.radius}px`,
        borderTop: `3px solid ${p.accent}`,
        p: 2,
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        {icon ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: `${p.radiusSm}px`,
              bgcolor: p.accentSoft,
              color: p.accent,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ fontSize: 18 }}>{icon}</Icon>
          </Box>
        ) : null}
        <MDTypography
          variant="caption"
          sx={{
            color: p.subtle,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </MDTypography>
      </Box>
      <MDTypography variant="h5" sx={{ color: p.ink, fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </MDTypography>
      {hint ? (
        <MDTypography variant="caption" sx={{ color: p.muted, display: "block", mt: 0.5 }}>
          {hint}
        </MDTypography>
      ) : null}
    </Box>
  );
}

PortalMetric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.node,
  icon: PropTypes.string,
};

PortalMetric.defaultProps = {
  hint: null,
  icon: null,
};

export function PortalButton({
  children,
  onClick,
  variant = "primary",
  size = "md",
  fullWidth,
  disabled,
  startIcon,
  endIcon,
  type,
  href,
  to,
  component,
}) {
  const p = usePortalTheme();
  const py = size === "sm" ? 0.65 : 1.1;
  const px = size === "sm" ? 1.25 : 2;
  const fontSize = size === "sm" ? "0.75rem" : "0.8125rem";

  const styles = {
    primary: {
      bgcolor: p.accent,
      color: "#fff",
      border: `1px solid ${p.accent}`,
      "&:hover": { bgcolor: p.accentHover },
    },
    secondary: {
      bgcolor: p.surface,
      color: p.ink,
      border: `2px solid ${p.ink}`,
      "&:hover": { bgcolor: p.ink, color: "#fff" },
    },
    ghost: {
      bgcolor: p.accentSoft,
      color: p.accent,
      border: `1px solid ${p.accentBorder}`,
      "&:hover": { bgcolor: p.accentSoft, borderColor: p.accent },
    },
    danger: {
      bgcolor: p.dangerSoft,
      color: p.danger,
      border: `1px solid rgba(215,38,30,0.18)`,
      "&:hover": { bgcolor: "rgba(215,38,30,0.12)" },
    },
    text: {
      bgcolor: "transparent",
      color: p.muted,
      border: "1px solid transparent",
      "&:hover": { color: p.ink, bgcolor: "rgba(17,19,24,0.04)" },
    },
  };

  return (
    <Box
      component={component || (href || to ? "a" : "button")}
      type={type || (href || to ? undefined : "button")}
      href={href}
      to={to}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      sx={{
        margin: 0,
        appearance: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : "auto",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        width: fullWidth ? "100%" : "auto",
        px,
        py,
        borderRadius: `${p.radiusSm}px`,
        fontWeight: 700,
        fontSize,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        textDecoration: "none",
        fontFamily: "inherit",
        transition: "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
        ...styles[variant],
      }}
    >
      {startIcon ? <Icon sx={{ fontSize: 18 }}>{startIcon}</Icon> : null}
      {children}
      {endIcon ? <Icon sx={{ fontSize: 18 }}>{endIcon}</Icon> : null}
    </Box>
  );
}

PortalButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(["primary", "secondary", "ghost", "danger", "text"]),
  size: PropTypes.oneOf(["sm", "md"]),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.string,
  endIcon: PropTypes.string,
  type: PropTypes.string,
  href: PropTypes.string,
  to: PropTypes.string,
  component: PropTypes.elementType,
};

PortalButton.defaultProps = {
  onClick: undefined,
  variant: "primary",
  size: "md",
  fullWidth: false,
  disabled: false,
  startIcon: null,
  endIcon: null,
  type: undefined,
  href: undefined,
  to: undefined,
  component: undefined,
};

export function PortalLinkRow({ title, meta, trailing, onClick }) {
  const p = usePortalTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        margin: 0,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        appearance: "none",
        font: "inherit",
        bgcolor: p.panel,
        border: `1px solid ${p.lineStrong}`,
        borderRadius: `${p.radiusSm}px`,
        p: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        transition: "border-color 160ms ease, background-color 160ms ease",
        "&:hover": { borderColor: p.accent, bgcolor: p.accentSoft },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <MDTypography variant="button" sx={{ color: p.ink, fontWeight: 800, display: "block" }}>
          {title}
        </MDTypography>
        {meta ? (
          <MDTypography variant="caption" sx={{ color: p.muted }}>
            {meta}
          </MDTypography>
        ) : null}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {trailing}
        <Icon sx={{ color: p.subtle, fontSize: 18 }}>chevron_right</Icon>
      </Box>
    </Box>
  );
}

PortalLinkRow.propTypes = {
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  trailing: PropTypes.node,
  onClick: PropTypes.func.isRequired,
};

PortalLinkRow.defaultProps = {
  meta: null,
  trailing: null,
};

export function PortalStatusChip({ label, tone = "neutral" }) {
  const p = usePortalTheme();
  const tones = {
    neutral: { bg: p.panel, color: p.muted, border: p.lineStrong },
    accent: { bg: p.accentSoft, color: p.accent, border: p.accentBorder },
    success: { bg: p.successSoft, color: p.success, border: "rgba(20,122,92,0.2)" },
    danger: { bg: p.dangerSoft, color: p.danger, border: "rgba(215,38,30,0.18)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: "auto",
        minHeight: 24,
        fontWeight: 700,
        fontSize: "0.6875rem",
        letterSpacing: "0.04em",
        bgcolor: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        "& .MuiChip-label": { px: 1, py: 0.35, whiteSpace: "normal" },
      }}
    />
  );
}

PortalStatusChip.propTypes = {
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["neutral", "accent", "success", "danger"]),
};

PortalStatusChip.defaultProps = {
  tone: "neutral",
};

export function PortalTable({ columns, rows, onRowClick, emptyLabel }) {
  const p = usePortalTheme();
  if (!rows?.length) {
    return (
      <PortalEmpty message={emptyLabel || "Nothing here yet."} />
    );
  }
  return (
    <Box
      sx={{
        bgcolor: p.surface,
        border: `1px solid ${p.lineStrong}`,
        borderRadius: `${p.radius}px`,
        overflow: "auto",
        boxShadow: p.shadow,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: p.panel }}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={{
                  fontWeight: 800,
                  fontSize: "0.6875rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: p.subtle,
                  borderBottom: `1px solid ${p.lineStrong}`,
                  whiteSpace: "nowrap",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              hover={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? "pointer" : "default",
                "&:hover": onRowClick ? { bgcolor: p.accentSoft } : undefined,
              }}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{ borderBottom: `1px solid ${p.line}`, color: p.ink }}
                >
                  {col.render ? col.render(row.data) : row.data[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

PortalTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      render: PropTypes.func,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      data: PropTypes.object.isRequired,
    }),
  ).isRequired,
  onRowClick: PropTypes.func,
  emptyLabel: PropTypes.string,
};

PortalTable.defaultProps = {
  onRowClick: null,
  emptyLabel: "",
};

export function PortalLoading({ label }) {
  const p = usePortalTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 3, color: p.muted }}>
      <CircularProgress size={20} sx={{ color: p.accent }} />
      <MDTypography variant="button" sx={{ color: p.muted, fontWeight: 600 }}>
        {label || "Loading…"}
      </MDTypography>
    </Box>
  );
}

PortalLoading.propTypes = {
  label: PropTypes.string,
};

PortalLoading.defaultProps = {
  label: "Loading…",
};

export function PortalEmpty({ message, action }) {
  const p = usePortalTheme();
  return (
    <Box
      sx={{
        py: 5,
        px: 2,
        textAlign: "center",
        bgcolor: p.surface,
        border: `1px dashed ${p.lineStrong}`,
        borderRadius: `${p.radius}px`,
      }}
    >
      <MDTypography variant="body2" sx={{ color: p.muted, mb: action ? 2 : 0 }}>
        {message}
      </MDTypography>
      {action}
    </Box>
  );
}

PortalEmpty.propTypes = {
  message: PropTypes.string.isRequired,
  action: PropTypes.node,
};

PortalEmpty.defaultProps = {
  action: null,
};

export function PortalAlert({ severity, children, sx }) {
  const p = usePortalTheme();
  const map = {
    error: { bg: p.dangerSoft, border: "rgba(215,38,30,0.18)", color: p.danger },
    info: { bg: p.accentSoft, border: p.accentBorder, color: p.accent },
    success: { bg: p.successSoft, border: "rgba(20,122,92,0.2)", color: p.success },
    warning: { bg: "rgba(255,167,38,0.1)", border: "rgba(255,167,38,0.35)", color: "#8F3700" },
  };
  const tone = map[severity] || map.info;
  return (
    <Alert
      severity={severity}
      sx={{
        mb: 2,
        borderRadius: `${p.radiusSm}px`,
        bgcolor: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.border}`,
        "& .MuiAlert-icon": { color: tone.color },
        ...sx,
      }}
    >
      {children}
    </Alert>
  );
}

PortalAlert.propTypes = {
  severity: PropTypes.string,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

PortalAlert.defaultProps = {
  severity: "info",
  sx: {},
};

export function PortalGrid({ children, spacing = 2 }) {
  return (
    <Grid container spacing={spacing}>
      {children}
    </Grid>
  );
}

PortalGrid.propTypes = {
  children: PropTypes.node.isRequired,
  spacing: PropTypes.number,
};

PortalGrid.defaultProps = {
  spacing: 2,
};

export function PortalGridItem({ children, xs = 12, md = 6 }) {
  return (
    <Grid item xs={xs} md={md}>
      {children}
    </Grid>
  );
}

PortalGridItem.propTypes = {
  children: PropTypes.node.isRequired,
  xs: PropTypes.number,
  md: PropTypes.number,
};

PortalGridItem.defaultProps = {
  xs: 12,
  md: 6,
};

export function PortalWorkflowTile({ icon, title, detail, onClick }) {
  const p = usePortalTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        margin: 0,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        appearance: "none",
        font: "inherit",
        bgcolor: p.surface,
        border: `1px solid ${p.lineStrong}`,
        borderRadius: `${p.radius}px`,
        borderTop: `3px solid ${p.accent}`,
        boxShadow: p.shadow,
        p: { xs: 2, md: 2.25 },
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease",
        "&:hover": {
          borderColor: p.accent,
          boxShadow: p.shadowLift,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: `${p.radiusSm}px`,
          bgcolor: p.accentSoft,
          color: p.accent,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon>{icon}</Icon>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <MDTypography variant="subtitle1" sx={{ color: p.ink, fontWeight: 800, lineHeight: 1.25 }}>
          {title}
        </MDTypography>
        {detail ? (
          <MDTypography variant="body2" sx={{ color: p.muted, mt: 0.5, lineHeight: 1.5 }}>
            {detail}
          </MDTypography>
        ) : null}
      </Box>
      <Icon sx={{ color: p.subtle, flexShrink: 0, mt: 0.25 }}>chevron_right</Icon>
    </Box>
  );
}

PortalWorkflowTile.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  detail: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

PortalWorkflowTile.defaultProps = {
  detail: "",
};

export function PortalWorkflowGrid({ items, onSelect }) {
  return (
    <PortalGrid spacing={2}>
      {items.map((item) => (
        <PortalGridItem key={item.route} xs={12} md={6} lg={4}>
          <PortalWorkflowTile
            icon={item.icon}
            title={item.label}
            detail={item.detail}
            onClick={() => onSelect(item.route)}
          />
        </PortalGridItem>
      ))}
    </PortalGrid>
  );
}

PortalWorkflowGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      route: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      detail: PropTypes.string,
    }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
};
